/*
 * product-ijewel-bridge.js — decoupled glue between the theme's variant
 * engine and the iJewel viewer runtime.
 *
 * The base variant engine (product-purchase-form.js) is NOT modified. This
 * bridge only *listens*:
 *   - `ijewel:viewer-ready`  → the WebGI viewer booted (product-model-ijewel.js)
 *   - `variant:update`       → shopper changed variant (bubbles on
 *                              `.product-purchase-form`; detail.resource = variant)
 *
 * Data comes from the page, not the DOM of the picker:
 *   - `window.iJewelVariantCadMap` = { "<variantId>": "<cad>" } — the geometry
 *     key. Seeded by product-ijewel-cad-map.liquid (no metaobject lookups, so
 *     it holds any number of distinct CADs) but capped at the FIRST 250
 *     VARIANTS, because Liquid's `product.variants` stops there. The
 *     `product-variants:loaded` handler below merges the rest in from the
 *     paginated matrix — without it, variants past 250 have no cad and the
 *     viewer sticks on the previous model (HEI-205).
 *   - `window.iJewelVariantMap`  = { "<variantId>": { url, cad }, ... } — the
 *     RESOLVED subset. Starts as just the selected variant (the seed map from
 *     product-ijewel-map-json.liquid) and grows as
 *     product-ijewel-cad-resolver.js resolves CADs on demand.
 *   - `.product-purchase-form__product-json` = {{ product | json }} — for the
 *     option names and the initial variant.
 *
 * Why two maps: Shopify resolves at most ~20 DISTINCT `metaobjects.cad_model`
 * entries per request, so a full variant → GLB map was silently truncated on
 * products with more CADs than that (Georgian Pinky Ring has 50, necklaces 40).
 * Cad codes are free to read; only the metaobject → GLB step is rationed, so
 * that step is deferred to one targeted request per CAD.
 *
 * Initial load: the seed map always carries the selected variant, so its GLB is
 * fetched immediately (parallel with WebGI boot) and applied as soon as the
 * viewer is ready — no round-trip, and a direct `?variant=` link works for any
 * CAD. After the first model paints, `ijewel:model-ready` arms
 * product-ijewel-prefetch.js.
 *
 * Swap logic (the unified path):
 *   - No cad for the variant → show nothing (empty). No fallback.
 *   - cad matches the loaded one → skip the reload AND skip resolution
 *     entirely, just apply config in place (metal / gem / etc.).
 *   - cad differs → take its url from the resolved map / resolver cache, or
 *     resolve it on demand, then load the new GLB and apply config.
 *   A resolve that loses the race to a newer selection is dropped via
 *   `applyGeneration`; `loadToken` is only taken after resolution, so a late
 *   response can never supersede the model the shopper actually chose.
 *   In every case the variant's full option configuration is (re)applied
 *   afterward, so the visible model always matches the selection — and when it
 *   cannot, the viewer is marked stale + warned rather than claiming `ready`.
 *
 * Config is fully dynamic: each product option NAME maps to an iJewel
 * variation category and the selected VALUE to the variation option, matched
 * fuzzily. Options with no matching authored variation (e.g. Size) no-op.
 */
// @ts-nocheck — vanilla JS that relies on custom window.* globals
// (iJewelVariantMap, iJewelActiveViewer) and the WebGI SDK, none of which the
// editor's implicit JS type-checker knows about. The theme ships no tsconfig.
(function () {
  'use strict';

  // Mobile / touch devices — notably iOS Safari, which exposes neither
  // navigator.deviceMemory nor navigator.connection, so the memory-aware
  // budgets below (preParseCap / preStageBudget) can't detect the phone and
  // default to their mid-tier settings. iOS Safari has a hard per-tab WebGL/JS
  // memory ceiling; speculatively pre-parsing sibling diamond GLBs on top of
  // the live model + bloom pipeline pushes it into webglcontextlost ("doesn't
  // load / freezes"). On mobile we therefore keep ONLY the active model
  // resident and let swaps parse on demand. Desktop keeps full look-ahead.
  var IS_MOBILE =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 767.98px), (pointer: coarse)').matches;

  // Per-Product-Group cache kill-switch: custom.product_family.disable_model_cache
  // (boolean on the product_group metaobject), exposed by
  // snippets/product-ijewel-viewer.liquid as window.__ijewelDisableModelCache.
  // When true this group keeps NO models resident beyond the one on stage —
  // desktop included: the parsed-model cache stores nothing (outgoing models are
  // disposed once safely off stage), speculative pre-parsing and idle
  // pre-staging are budgeted to zero. Every shape swap is then a cold
  // fetch+parse, covered by the SDK loading screen (product-model-ijewel.js).
  // Default off — unflagged groups keep the standard budgets below.
  var DISABLE_MODEL_CACHE = window.__ijewelDisableModelCache === true;

  var root, container, loadingEl, emptyEl;
  var variantMap = {};
  // Layer A: variantId -> cad handle, for EVERY variant. Emitted by
  // snippets/product-ijewel-cad-map.liquid with no metaobject lookups, so it is
  // complete even on products whose 40-50 distinct CADs blow past Shopify's
  // ~20-distinct-metaobjects-per-request ceiling. Geometry-change detection and
  // prefetch ranking read this; GLB urls arrive per-cad via IjewelCadResolver.
  var variantCadMap = {};
  var optionNames = [];
  var productVariants = [];
  var productTitle = '';
  var currentCad; // handle of the loaded model; null = intentionally empty
  var loadToken = 0; // rapid-switch guard: only the latest load wins
  var pendingVariant = null;
  var viewerReady = false;

  // Parsed-model cache: cad -> array of scene objects. Swapping to a cached
  // cad is a scene-graph re-add (no fetch, no parse) — the "zero loading"
  // path. Bounded so bracelet families (16 GLBs) can't exhaust GPU memory.
  var modelCache = {};
  var modelCacheOrder = []; // LRU, oldest first
  // Bracelet families run to 16 GLBs; 8 resident is safe on desktop but too
  // much for a mobile memory budget. Cap tighter on mobile — LRU evicts.
  var MODEL_CACHE_LIMIT = IS_MOBILE ? 3 : 8;
  // Settle window for the first-stage canvas freeze (see swapModel): masks a
  // measured WebGi 0.15 defect — one full-canvas dark frame is presented
  // exactly once ~15 rendered frames (~230ms at 60fps) after a GLB's FIRST
  // scene add. The mask must outlive it: lift only after >=28 postFrames AND
  // >=500ms (2s hard cap below). Shrinking these re-exposes the flash —
  // verified by per-frame canvas sampling.
  var UNFREEZE_MIN_FRAMES = 28;
  var UNFREEZE_MIN_MS = 500;
  var UNFREEZE_TIMEOUT_MS = 2000;
  // Instant swap micro-guard: even when a cad is already staged, hold reveal
  // for ~2 rendered frames so one-frame renderer/material pops stay hidden.
  var QUICK_REVEAL_MIN_FRAMES = 2;
  var QUICK_REVEAL_MIN_MS = 34;
  var QUICK_REVEAL_TIMEOUT_MS = 120;
  // cads whose objects have been ADDED to the scene at least once. The
  // engine's corrupt-frame refresh fires on an asset's first ADD — not its
  // import — so `modelCache` membership is the wrong gate: the prefetcher
  // pre-parses GLBs into modelCache without ever staging them.
  var stagedCads = {};
  // Idle pre-staging: pay each sibling shape's first-add corrupt frame before the
  // shopper clicks, so click-time swaps hit the already-staged fast path.
  var preStageQueue = [];
  var preStageDraining = false;

  // Framing / debug test panel sets window.__ijewelBridgeSuppressed = true so
  // we never stage the live product GLB alongside a manually loaded test file.
  // Bumping loadToken aborts any swap that already started before the flag.
  function isBridgeSuppressed() {
    return window.__ijewelBridgeSuppressed === true;
  }
  window.__ijewelBumpLoadToken = function () {
    loadToken++;
  };
  var preStageSessionActive = true;
  // Hotfix: keep idle pre-staging disabled until wrong-GLB regression is fully
  // diagnosed in production parity. Swap path still uses cache + settle guards.
  var ENABLE_IDLE_PRESTAGE = false;
  var lastViewerInteraction = 0;
  var INTERACTION_COOLDOWN_MS = 1000;
  // For first load, keep "loading" until the canvas is revealed so the spinner
  // crossfades into a clean frame. `stageRevealToken` records WHICH swap opened
  // that window (its loadToken): the flag gates `setState('ready')` for every
  // apply, so without an owner a reveal left pending by a superseded first load
  // silently suppresses the ready of the swap that replaced it — the viewer then
  // stays `is-loading` (canvas opacity 0) until the next option change. See the
  // ownership checks in stageFirstLoad() and applyVariant().
  var stageRevealPending = false;
  var stageRevealToken = -1;
  var preParseQueue = [];
  var preParseBusy = false;
  var preParseSeen = {};
  var PREPARSE_MAX_HIGH = 8;
  var PREPARSE_MAX_LOW = 4;
  var preParseTelemetry = {
    cap: 0,
    queued: 0,
    prioritizedQueued: 0,
    skippedSeen: 0,
    skippedCached: 0,
    parsed: 0,
    failed: 0,
  };
  var applyChain = Promise.resolve();
  var applyGeneration = 0;
  var firstModelReady = false;

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    root = document.querySelector('[data-ijewel-root]');
    if (!root) return;

    container = root.querySelector('.ijewel-viewer-container');
    loadingEl = root.querySelector('[data-ijewel-loading]');
    emptyEl = root.querySelector('[data-ijewel-empty]');

    variantMap = normalizeVariantMapUrls(window.iJewelVariantMap || {});
    variantCadMap = window.iJewelVariantCadMap || {};

    var product = readProductJson();
    if (product) {
      productVariants = product.variants || [];
      productTitle = product.title || '';
      optionNames = (product.options || []).map(function (o) {
        return typeof o === 'string' ? o : (o && o.name) || '';
      });
    }

    pendingVariant = resolveInitialVariant();

    // Fetch the selected GLB in parallel with viewer boot — the seed map always
    // carries it, so this starts before WebGI finishes initializing.
    //
    // Skipped when the Product Group opts out of 3D on mobile: the viewer never
    // boots on phones there (product-model-ijewel.js), so warming its model is
    // pure waste — and on the heaviest groups it is the 8.5MB download the flag
    // exists to prevent. Measured before this guard: 1.7MB still fetched on a
    // phone with the viewer correctly disabled.
    var ijewelRoot = document.querySelector('[data-ijewel-root]');
    if (!(IS_MOBILE && ijewelRoot && ijewelRoot.closest('[data-ijewel-desktop-only]'))) {
      warmInitialGlb();
    }

    if (window.iJewelActiveViewer) {
      viewerReady = true;
      tryApplyPending();
    }

    window.addEventListener('ijewel:viewer-ready', function () {
      viewerReady = true;
      tryApplyPending();
      drainPreParse();
    });

    document.addEventListener('variant:update', function (event) {
      var variant = event && event.detail && event.detail.resource;
      if (!variant) return;
      pendingVariant = variant;
      if (!viewerReady) return;

      syncVariantMap();
      // applyVariant now resolves the CAD itself (cache hit → targeted request),
      // so there is no full-map load to wait on. A same-CAD change (Stone Type)
      // short-circuits inside applyVariant with no network at all.
      scheduleApplyVariant(resolveVariantFor3D(variant) || variant);
      schedulePreStageDrain();
    });

    /* The `ijewel:map-loaded` listener that used to sit here is gone with the
       paginated full map that dispatched it. applyVariant() now awaits the CAD
       it needs, so there is no "map grew, re-apply the pending selection" gap to
       cover — and re-applying on every background resolve would re-run the
       material config once per warmed CAD for a model that never changed. */

    /* Layer A completion (HEI-205). product-ijewel-cad-map.liquid loops
       `product.variants`, which Liquid caps at 250, so on a >250-variant
       product the cad map ships truncated — Signature 5-Stone Ring has 480
       variants and 230 of them (every coloured stone type, which sort after
       the cap) arrived with no cad. applyVariant() then took its "variant has
       no custom.cad_code" branch and setStaleState() held the previous
       geometry on screen, which is the "render gets stuck" report.

       The matrix that product-purchase-form.js already fetches is paginated
       and uncapped, so merging its `cad` field repairs the map. It only fires
       on products where the form decided a full load was needed (total >
       embedded), i.e. exactly the >250-variant products that are broken.

       The re-apply is deliberately narrow — ONLY when the CURRENT selection
       was the thing missing a cad. Re-applying unconditionally is what the
       removed `ijewel:map-loaded` listener did, and it re-ran the material
       config for models that never changed. */
    document.addEventListener('product-variants:loaded', function (event) {
      var variants = (event && event.detail && event.detail.variants) || [];
      // Sample BEFORE the merge — afterwards the gap it repaired is invisible.
      var wasUnmapped = !!pendingVariant && !cadForVariant(pendingVariant);
      if (!mergeCadMapFromMatrix(variants)) return;
      if (!wasUnmapped || !viewerReady) return;
      if (!cadForVariant(pendingVariant)) return; // genuinely has no model
      scheduleApplyVariant(resolveVariantFor3D(pendingVariant) || pendingVariant);
      schedulePreStageDrain();
    });

    // Combined-listing metal switch: a sibling product's data was swapped in
    // place (new product-json node text + new window.iJewelVariantMap). Re-read
    // the context so the follow-up `variant:update` resolves against the new
    // map/title/variants. We deliberately DO NOT reset `currentCad`: metal is a
    // material change, not a geometry change — the matched sibling variant
    // carries the SAME cad_code, so preserving currentCad lets applyVariant()
    // skip the GLB reload and only re-apply config (metal from the new title).
    document.addEventListener('ijewel:refresh-context', function () {
      refreshContext();
      // Metal siblings share cad codes AND GLB urls — only variant ids differ.
      // Re-apply everything already resolved onto the new product's ids so a
      // metal switch re-downloads nothing.
      if (window.IjewelCadResolver) window.IjewelCadResolver.recommit();
      syncVariantMap();
    });

    // product-ijewel-prefetch.js network-warmed a GLB → pre-parse it into the
    // model cache during idle time so the eventual swap is a same-frame toggle.
    document.addEventListener('ijewel:glb-warmed', function (event) {
      var url = event && event.detail && event.detail.url;
      // On mobile, skip speculative pre-parsing entirely — this bypasses
      // preParseCap(), so gating it here is what actually keeps warmed sibling
      // GLBs out of GPU/JS memory. The swap parses on demand instead.
      // Also wait for the first staged model: warmInitialGlb() fires this for the
      // selected URL while swapModel is already importing it, and a parallel
      // pre-parse of the same GLB contending with first-stage add delays reveal
      // on heavy products (many distinct CADs competing for the main thread once
      // siblings start resolving). Sibling warms arrive after model-ready.
      // DISABLE_MODEL_CACHE groups also skip this: like the mobile gate above,
      // this listener bypasses preParseCap(), so it must be gated here too or
      // warmed sibling GLBs would still be parsed into memory.
      if (url && !IS_MOBILE && !DISABLE_MODEL_CACHE && firstModelReady) schedulePreParse(url);
    });

    // Viewer torn down (product-model-ijewel.js disconnectedCallback / mid-init
    // unwind). Every cached scene object belongs to the now-disposed viewer, so
    // drop all viewer-coupled state: keeping modelCache/stagedCads would let a
    // re-created viewer re-stage disposed three.js objects. Bump loadToken so any
    // in-flight swap is superseded; a fresh viewer re-imports everything cleanly.
    window.addEventListener('ijewel:viewer-destroyed', function () {
      viewerReady = false;
      loadToken++;
      modelCache = {};
      modelCacheOrder = [];
      stagedCads = {};
      preParseQueue = [];
      preParseBusy = false;
      preParseSeen = {};
      preParseTelemetry = {
        cap: 0,
        queued: 0,
        prioritizedQueued: 0,
        skippedSeen: 0,
        skippedCached: 0,
        parsed: 0,
        failed: 0,
      };
      currentCad = null;
      firstModelReady = false;
      stageRevealPending = false;
      stageRevealToken = -1;
      preStageQueue = [];
      preStageDraining = false;
      preStageSessionActive = true;
      lastViewerInteraction = 0;
      publishPreParseTelemetry();
    });

    if (container) {
      ['pointerdown', 'touchstart', 'wheel'].forEach(function (evt) {
        container.addEventListener(evt, markViewerInteraction, { passive: true });
      });
    }
  }

  // Shopify Model3d URLs are often protocol-relative. On http:// origins
  // (theme-dev) that becomes http:// and Safari refuses the CDN's
  // http→https CORS redirect — importer yields no objects / empty canvas.
  // Liquid already forces https; this keeps runtime-merged maps safe too.
  function normalizeAssetUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (url.indexOf('//') === 0) return 'https:' + url;
    if (url.indexOf('http://') === 0) return 'https://' + url.slice(7);
    return url;
  }

  function normalizeVariantMapUrls(map) {
    if (!map) return map;
    var ids = Object.keys(map);
    for (var i = 0; i < ids.length; i++) {
      var entry = map[ids[i]];
      if (entry && entry.url) entry.url = normalizeAssetUrl(entry.url);
    }
    return map;
  }

  function syncVariantMap() {
    variantMap = normalizeVariantMapUrls(window.iJewelVariantMap || variantMap || {});
    variantCadMap = window.iJewelVariantCadMap || variantCadMap || {};
  }

  /* Fold the paginated `?view=variants` matrix into Layer A.
     product-ijewel-cad-map.liquid builds that map by looping
     `product.variants`, which Liquid caps at 250 — so on a >250-variant
     product every variant past the cap arrives with NO cad and the viewer
     freezes on the previous geometry (HEI-205). The matrix is paginated and
     uncapped, and now carries the same handleized `cad`, so it is the
     completion source. Returns how many entries were added or corrected. */
  function mergeCadMapFromMatrix(variants) {
    if (!variants || !variants.length) return 0;
    if (!window.iJewelVariantCadMap) window.iJewelVariantCadMap = {};
    var map = window.iJewelVariantCadMap;
    var changed = 0;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v || v.id == null || !v.cad) continue;
      var id = String(v.id);
      if (map[id] === v.cad) continue;
      map[id] = v.cad;
      changed++;
    }
    if (changed) syncVariantMap();
    return changed;
  }

  /* Self-heal a short cad map by pulling the paginated matrix directly.
     Deduped per handle, so a variant that genuinely has no cad_code still
     lands on the "no model" path instead of refetching on every selection.

     Needed because `product-variants:loaded` does not cover the combined-
     listing metal switch: product-group-selector.js REPLACES
     window.iJewelVariantCadMap with the sibling's map, and
     templates/product.metal-switch.liquid builds THAT from `product.variants`
     too — so it arrives capped at 250 exactly like the seed, and the merged
     map we had built for the previous metal is thrown away. Rather than
     chasing every path that can shorten the map, applyVariant asks for the
     repair the moment it finds a variant with no cad. */
  var cadRepairs = {}; // handle -> Promise<number of entries merged>
  function ensureCadMapForHandle() {
    var handle = productHandle();
    var matrix = window.ProductVariantMatrix;
    if (!handle || !matrix || typeof matrix.loadAll !== 'function') return Promise.resolve(0);
    if (cadRepairs[handle]) return cadRepairs[handle];
    cadRepairs[handle] = matrix
      .loadAll(handle)
      .then(function (result) {
        return mergeCadMapFromMatrix((result && result.variants) || []);
      })
      .catch(function () {
        delete cadRepairs[handle]; // a later selection may legitimately retry
        return 0;
      });
    return cadRepairs[handle];
  }

  function formVariantId() {
    var input = document.querySelector(
      '.product-purchase-form [data-product-form-variant-id], .product-purchase-form [name="id"]'
    );
    return input && input.value;
  }

  function isCurrentFormVariant(variant) {
    if (!variant) return false;
    var id = formVariantId();
    return id && String(id) === String(variant.id);
  }

  /** Serialize applies; drop superseded/stale selections. */
  function scheduleApplyVariant(variant) {
    // Framing test panel owns the stage once it loads a test piece — don't
    // even queue variant applies while suppressed (applyVariant re-checks too).
    if (isBridgeSuppressed()) return applyChain;
    if (!variant || !isCurrentFormVariant(variant)) return applyChain;
    var gen = ++applyGeneration;
    var target = variant;
    applyChain = applyChain
      .then(function () {
        if (gen !== applyGeneration) return;
        if (!isCurrentFormVariant(target)) return;
        return applyVariant(target);
      })
      .catch(function (error) {
        console.error('iJewel 3D: apply failed', error);
      });
    return applyChain;
  }

  /** Apply as soon as the viewer is up and we know the variant's geometry. */
  function tryApplyPending() {
    if (!pendingVariant || !viewerReady) return;
    syncVariantMap();
    // The seed map carries the selected variant's url outright; for anything
    // else Layer A gives us the cad and applyVariant resolves its GLB on demand.
    // Bailing here used to leave the spinner up forever for unmapped variants.
    if (!cadForVariant(pendingVariant)) return;
    scheduleApplyVariant(pendingVariant);
  }

  // The head preload in snippets/product-ijewel-preload.liquid normally makes
  // this a preload-cache hit; kept as the fallback when preload hints are
  // stripped (extensions, data-saver). Fetch options must stay
  // `mode: 'cors', credentials: 'same-origin'` to match the preload's
  // crossorigin="anonymous" — per the fetch spec crossorigin="anonymous" maps
  // to credentials mode "same-origin" (NOT "omit"); `credentials: 'omit'` here
  // was a mismatch that double-downloaded the ~2MB GLB on every PDP load
  // (Chrome: "preload … not used because the request credentials mode does not
  // match").
  function warmInitialGlb() {
    var variant = pendingVariant;
    if (!variant) return;
    var entry = (window.iJewelVariantMap || {})[String(variant.id)];
    if (!entry || !entry.url) return;
    var url = normalizeAssetUrl(entry.url);
    entry.url = url;
    fetch(url, { mode: 'cors', credentials: 'same-origin', priority: 'high' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.blob();
      })
      .then(function () {
        document.dispatchEvent(
          new CustomEvent('ijewel:glb-warmed', { detail: { url: url } })
        );
      })
      .catch(function () {});
  }

  function notifyFirstModelReady() {
    if (firstModelReady) return;
    firstModelReady = true;
    document.dispatchEvent(new CustomEvent('ijewel:model-ready'));
    queuePriorityPreParse();
    attachWebglLossGuard();
    schedulePreStageDrain();
  }

  function queuePriorityPreParse() {
    if (!viewerReady) return;
    syncVariantMap();
    var variant = resolveVariantFor3D(pendingVariant) || pendingVariant;
    if (!variant) return;

    var cap = preParseCap();
    var urls = orderedPreParseUrlsForVariant(variant).slice(0, cap);
    preParseTelemetry.cap = cap;
    for (var i = 0; i < urls.length; i++) {
      // Front-load shape swaps for the currently selected variant family.
      schedulePreParse(urls[i], { priority: i < 4 });
    }
    publishPreParseTelemetry();
  }

  function preParseCap() {
    if (DISABLE_MODEL_CACHE) return 0; // group opted out of residency entirely
    if (IS_MOBILE) return 0; // keep only the active model resident on mobile
    var mem = navigator.deviceMemory;
    if (mem != null && mem <= 2) return PREPARSE_MAX_LOW;
    if (mem == null || mem < 4) return PREPARSE_MAX_LOW;
    return PREPARSE_MAX_HIGH;
  }

  function orderedPreParseUrlsForVariant(variant) {
    var map = variantMap || {};
    var shapePos = shapeOptionPosition();
    var ids = Object.keys(map);
    var variantsById = {};
    productVariants.forEach(function (v) {
      variantsById[String(v.id)] = v;
    });

    var shapeFirstByCad = {};
    var restByCad = {};

    for (var i = 0; i < ids.length; i++) {
      var id = ids[i];
      var entry = map[id];
      if (!entry || !entry.url || !entry.cad || entry.cad === currentCad) continue;
      if (stagedCads[entry.cad] || modelCache[entry.cad]) continue;

      var candidate = variantsById[id];
      if (!candidate) continue;

      var diffs = [];
      for (var p = 1; p <= optionNames.length; p++) {
        if (variantOptionValue(candidate, p) !== variantOptionValue(variant, p)) diffs.push(p);
      }
      if (!diffs.length) continue;

      // "Current variant first" means shape siblings (same everything else) first.
      if (shapePos && diffs.length === 1 && diffs[0] === shapePos) {
        if (!shapeFirstByCad[entry.cad]) shapeFirstByCad[entry.cad] = entry.url;
      } else if (!restByCad[entry.cad]) {
        restByCad[entry.cad] = entry.url;
      }
    }

    return Object.keys(shapeFirstByCad)
      .map(function (cad) {
        return shapeFirstByCad[cad];
      })
      .concat(
        Object.keys(restByCad).map(function (cad) {
          return restByCad[cad];
        })
      );
  }

  function markViewerInteraction() {
    lastViewerInteraction = Date.now();
  }

  function viewerIsIdle() {
    return Date.now() - lastViewerInteraction >= INTERACTION_COOLDOWN_MS;
  }

  function preStageBudget() {
    if (DISABLE_MODEL_CACHE) return 0; // group opted out of residency entirely
    if (IS_MOBILE) return 0; // no idle pre-staging on mobile (memory ceiling)
    var mem = navigator.deviceMemory;
    if (mem != null && mem <= 2) return 0;
    if (mem == null || mem < 4) return 2;
    return Infinity;
  }

  function shapeOptionPosition() {
    for (var i = 0; i < optionNames.length; i++) {
      if (/shape/i.test(optionNames[i] || '')) return i + 1;
    }
    return null;
  }

  function variantOptionDistance(fromVariant, toVariant) {
    if (!fromVariant || !toVariant) return optionNames.length || 1;
    var dist = 0;
    for (var p = 1; p <= optionNames.length; p++) {
      if (variantOptionValue(fromVariant, p) !== variantOptionValue(toVariant, p)) dist++;
    }
    return dist;
  }

  function rankPreStageCads() {
    var budget = preStageBudget();
    if (!budget || !firstModelReady || !viewerReady || !preStageSessionActive) return [];

    syncVariantMap();
    var variant = pendingVariant;
    if (!variant || !currentCad || modelRootChildren().length === 0) return [];

    var shapePos = shapeOptionPosition();
    var byCad = {};
    var ids = Object.keys(variantMap);
    for (var i = 0; i < ids.length; i++) {
      var entry = variantMap[ids[i]];
      if (!entry || !entry.cad || !entry.url) continue;
      if (entry.cad === currentCad || stagedCads[entry.cad]) continue;
      if (!modelCache[entry.cad]) continue;

      var match = productVariants.find(function (v) {
        return String(v.id) === ids[i];
      });
      if (!match) continue;

      var dist;
      if (shapePos) {
        if (variantOptionValue(match, shapePos) === variantOptionValue(variant, shapePos)) continue;
        dist = 1;
      } else {
        dist = variantOptionDistance(variant, match);
        if (!dist) continue;
      }

      var prev = byCad[entry.cad];
      if (!prev || dist < prev.dist) byCad[entry.cad] = { cad: entry.cad, dist: dist };
    }

    var ranked = Object.keys(byCad)
      .map(function (cad) {
        return byCad[cad];
      })
      .sort(function (a, b) {
        return a.dist - b.dist;
      });

    if (budget !== Infinity) ranked = ranked.slice(0, budget);
    return ranked.map(function (item) {
      return item.cad;
    });
  }

  function schedulePreStageDrain() {
    if (!ENABLE_IDLE_PRESTAGE) return;
    if (!firstModelReady || !viewerReady || !preStageSessionActive || stageRevealPending) return;
    preStageQueue = rankPreStageCads();
    drainPreStage();
  }

  function drainPreStage() {
    if (preStageDraining || !preStageSessionActive || !firstModelReady || !viewerReady) return;

    var cad = preStageQueue.shift();
    if (!cad) return;
    if (stagedCads[cad] || cad === currentCad) {
      drainPreStage();
      return;
    }
    if (!modelCache[cad]) {
      drainPreStage();
      return;
    }
    if (!viewerIsIdle()) {
      preStageQueue.unshift(cad);
      setTimeout(drainPreStage, 300);
      return;
    }

    preStageDraining = true;
    var idle = window.requestIdleCallback || function (fn) {
      setTimeout(fn, 200);
    };
    idle(function () {
      preStageCad(cad)
        .catch(function () {})
        .then(function () {
          preStageDraining = false;
          drainPreStage();
        });
    }, { timeout: 3000 });
  }

  async function preStageCad(cad) {
    if (
      isBridgeSuppressed() ||
      !preStageSessionActive ||
      !viewerReady ||
      stagedCads[cad] ||
      cad === currentCad ||
      stageRevealPending
    ) {
      return;
    }
    if (!viewerIsIdle()) return;

    var viewer = window.iJewelActiveViewer;
    if (!viewer || modelRootChildren().length === 0) return;

    var objects = modelCache[cad];
    if (!objects || !objects.length) return;

    var token = loadToken;
    var unfreeze = freezeCanvas(viewer);
    if (!unfreeze) return;
    var settleHandled = false;

    try {
      await runWithInstantTransitions(viewer, async function () {
        var stale = modelRootChildren();
        var added = [];
        function syncAddedFromScene(hide) {
          modelRootChildren().forEach(function (child) {
            if (stale.indexOf(child) !== -1 || added.indexOf(child) !== -1) return;
            if (hide) child.visible = false;
            added.push(child);
          });
        }

        for (var i = 0; i < objects.length; i++) {
          var beforeOne = modelRootChildren();
          await addToScene(viewer, objects[i]);
          modelRootChildren().forEach(function (child) {
            if (
              beforeOne.indexOf(child) === -1 &&
              stale.indexOf(child) === -1 &&
              added.indexOf(child) === -1
            ) {
              child.visible = false;
              added.push(child);
            }
          });
          // Some SDK paths materialize root children after addSceneObject
          // resolves; capture and hide any late arrivals before they can flash.
          syncAddedFromScene(true);
        }

        if (token !== loadToken) {
          added.forEach(function (child) {
            viewer.scene.modelRoot.remove(child);
          });
          return;
        }

        prepareDiamondMeshes(viewer, added);
        await precompile(viewer, added);
        syncAddedFromScene(true);

        if (token !== loadToken) {
          added.forEach(function (child) {
            viewer.scene.modelRoot.remove(child);
          });
          return;
        }

        await waitSettleWindow(viewer, unfreeze);
        settleHandled = true;

        if (token !== loadToken) {
          added.forEach(function (child) {
            viewer.scene.modelRoot.remove(child);
          });
          return;
        }

        added.forEach(function (child) {
          viewer.scene.modelRoot.remove(child);
        });
      });
    } catch (_e) {
      return;
    } finally {
      if (!settleHandled && unfreeze) unfreeze();
    }

    if (token === loadToken && preStageSessionActive) {
      stagedCads[cad] = true;
      touchCache(cad);
    }
  }

  function attachWebglLossGuard() {
    var viewer = window.iJewelActiveViewer;
    if (!viewer || viewer.__ijewelPreStageLossBound) return;
    var canvas =
      viewer.canvas || (container && container.querySelector('canvas'));
    if (!canvas) return;
    viewer.__ijewelPreStageLossBound = true;
    canvas.addEventListener('webglcontextlost', function () {
      preStageSessionActive = false;
      preStageQueue = [];
      preStageDraining = false;
    });
  }

  // Normalise a Vector3-or-[x,y,z] into a plain triple (copies values, holds no
  // live reference to the camera's own vectors).
  function vecToArr(v) {
    if (!v) return null;
    if (Array.isArray(v)) return v.length >= 3 ? [v[0], v[1], v[2]] : null;
    if (typeof v.x === 'number') return [v.x, v.y, v.z];
    return null;
  }

  // Snapshot the active camera's position + orbit target so it can be re-applied
  // after a model swap (the swap's GLB import resets the camera to its export).
  function captureCameraPose() {
    try {
      var vw = window.iJewelActiveViewer;
      var cam = vw && vw.scene && vw.scene.activeCamera;
      if (!cam) return null;
      var co = typeof cam.getCameraOptions === 'function' ? cam.getCameraOptions() : null;
      var pos = vecToArr(cam.position) || vecToArr(co && co.position);
      var target = vecToArr(co && co.target) || vecToArr(cam.target);
      return pos && target ? { position: pos, target: target } : null;
    } catch (_e) {
      return null;
    }
  }

  function restoreCameraPose(pose) {
    try {
      var vw = window.iJewelActiveViewer;
      var cam = vw && vw.scene && vw.scene.activeCamera;
      if (!cam || !pose || typeof cam.setCameraOptions !== 'function') return;
      cam.setCameraOptions({ position: pose.position.slice(), target: pose.target.slice() });
      setDirty(vw);
    } catch (_e) {
      /* best effort — keep whatever the swap left */
    }
  }

  function productHandle() {
    return (
      root?.dataset?.productHandle ||
      document.querySelector('.product-purchase-form__product-json')?.dataset?.productHandle ||
      readProductJson()?.handle ||
      ''
    );
  }

  /* The paginated full-map loader that used to live here is gone. It fetched
     `?view=ijewel-map` to fill in variants past the 250-variant inline cap, but
     every page of that endpoint was itself capped at ~20 distinct metaobjects,
     so it could never complete the set it existed to complete. Coverage now
     comes from Layer A (all variants' cad codes, no metaobject lookups) plus
     per-CAD resolution in product-ijewel-cad-resolver.js, which has no ceiling
     and no 250-variant limit. */

  function refreshContext() {
    variantMap = window.iJewelVariantMap || {};
    variantCadMap = window.iJewelVariantCadMap || {};
    var product = readProductJson();
    if (!product) return;
    productVariants = product.variants || [];
    productTitle = product.title || '';
    optionNames = (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : (o && o.name) || '';
    });
  }

  // Parse cache keyed on the raw JSON text, so it self-invalidates when the
  // node changes (a combined-listing metal switch re-renders the section and
  // swaps in a new product-json blob) — no explicit bust needed on repoint.
  var productJsonRaw = null;
  var productJsonParsed = null;

  function readProductJson() {
    var node = document.querySelector('.product-purchase-form__product-json');
    if (!node) return null;
    var raw = node.textContent;
    if (raw === productJsonRaw) return productJsonParsed;
    try {
      productJsonParsed = JSON.parse(raw);
      productJsonRaw = raw;
      return productJsonParsed;
    } catch (_e) {
      return null;
    }
  }

  /* Initial variant: whatever the purchase form's hidden id input holds
     (it syncs from ?variant= on load), falling back to the first variant. */
  function resolveInitialVariant() {
    var input = document.querySelector(
      '.product-purchase-form [data-product-form-variant-id], .product-purchase-form [name="id"]'
    );
    var id = input && input.value;
    if (id) {
      var match = productVariants.find(function (v) {
        return String(v.id) === String(id);
      });
      if (match) return match;
    }
    return productVariants[0] || null;
  }

  function variantOptionValue(variant, position) {
    if (window.ProductVariantMatrix && window.ProductVariantMatrix.optionValue) {
      return window.ProductVariantMatrix.optionValue(variant, position);
    }
    return variant && variant['option' + position];
  }

  function readFormOptionValue(position) {
    var nodes = document.querySelectorAll(
      '.product-purchase-form [data-option-select][data-option-position="' + position + '"]'
    );
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.tagName === 'CUSTOM-SELECT-COMPONENT') {
        var hidden = el.querySelector('input[type="hidden"]');
        if (hidden && hidden.value) return hidden.value;
      } else if (el.tagName === 'SELECT' && el.value) {
        return el.value;
      } else if (el.type === 'radio') {
        if (el.checked && el.value) return el.value;
      } else if (el.type === 'hidden' && el.value) {
        return el.value;
      }
    }
    return null;
  }

  function enrichVariantFromForm(variant) {
    var out = Object.assign({}, variant || {});
    for (var i = 0; i < optionNames.length; i++) {
      var pos = i + 1;
      var key = 'option' + pos;
      var fromForm = readFormOptionValue(pos);
      if (fromForm) {
        out[key] = fromForm;
        continue;
      }
      if (!out[key]) {
        var fromVariant = variantOptionValue(out, pos);
        if (fromVariant) out[key] = fromVariant;
      }
    }
    return out;
  }

  /** Authoritative variant for 3D: form selection + sibling matrix entry by id. */
  function resolveVariantFor3D(variant) {
    var idInput = document.querySelector(
      '.product-purchase-form [data-product-form-variant-id], .product-purchase-form [name="id"]'
    );
    var id = idInput && idInput.value;
    // Fast-resolve variants (past the embedded 250 cap) only exist on the
    // event payload — prefer it when the form id already matches.
    if (id && variant && String(variant.id) === String(id)) {
      return enrichVariantFromForm(variant);
    }
    if (id) {
      var fromMatrix = productVariants.find(function (v) {
        return String(v.id) === String(id);
      });
      if (fromMatrix) return enrichVariantFromForm(fromMatrix);
    }
    return enrichVariantFromForm(variant);
  }

  function collectConfigValues(variant) {
    var values = [];
    var seen = {};
    for (var i = 0; i < optionNames.length; i++) {
      var name = optionNames[i];
      var value = variantOptionValue(variant, i + 1);
      if (!value) continue;
      if (name && name.indexOf(':') !== -1) {
        String(value)
          .split(' / ')
          .forEach(function (part) {
            part = part.trim();
            if (part && !seen[part]) {
              seen[part] = true;
              values.push(part);
            }
          });
      } else if (!seen[value]) {
        seen[value] = true;
        values.push(value);
      }
    }
    return values;
  }

  /** cad handle for a variant: resolved map first, then Layer A. */
  function cadForVariant(variant) {
    if (!variant) return '';
    var id = String(variant.id);
    var entry = variantMap[id];
    if (entry && entry.cad) return entry.cad;
    if (variantCadMap[id]) return variantCadMap[id];
    var resolver = window.IjewelCadResolver;
    return (resolver && resolver.cadForVariant(id)) || '';
  }

  /* Re-apply config/materials to whatever geometry is already staged, and
     declare ready. Only legitimate when the staged cad IS this variant's cad. */
  async function applyConfigOnStagedGeometry(variant) {
    await applyConfigFromOptions(variant);
    prepareDiamondMeshes(window.iJewelActiveViewer, modelRootChildren());
    if (currentCad) stagedCads[currentCad] = true;
    if (typeof window.__ijewelEnsureDesktopBloom === 'function') {
      window.__ijewelEnsureDesktopBloom(window.iJewelActiveViewer);
    }
  }

  /* The selection could not be given its own geometry. Keep the last valid
     model up rather than blanking the viewer, but never claim `ready` — the
     shopper would be looking at another CAD while the UI says it loaded. Clears
     the spinner so nothing hangs unexplained, and leaves a machine-readable
     marker plus a console warning instead of failing silently (the old
     behaviour, which is what made this class of bug invisible for so long). */
  function setStaleState(variant, wantedCad, reason) {
    if (!root) return;
    root.classList.remove('is-loading', 'is-empty');
    if (loadingEl) loadingEl.hidden = true;
    if (emptyEl) emptyEl.hidden = true;
    root.dataset.ijewelStaleCad = wantedCad || '';
    console.warn('iJewel 3D: showing previous geometry — selection did not load', {
      variantId: variant && variant.id,
      wantedCad: wantedCad || '(none)',
      stagedCad: currentCad || '(none)',
      reason: reason,
    });
  }

  function clearStaleState() {
    if (root && root.dataset.ijewelStaleCad != null) delete root.dataset.ijewelStaleCad;
  }

  async function applyVariant(variant) {
    if (isBridgeSuppressed()) return;
    if (!isCurrentFormVariant(variant)) return;
    syncVariantMap();
    variant = resolveVariantFor3D(variant);

    var targetCad = cadForVariant(variant);
    var onStage = modelRootChildren().length > 0;

    // Layer A can be SHORT. Both the seed map and the metal-switch map are
    // built from `product.variants`, which Liquid caps at 250, so on a bigger
    // product a legitimate variant can look like it has no model. Repair from
    // the uncapped matrix before concluding anything (HEI-205).
    if (!targetCad) {
      var repairGen = applyGeneration;
      await ensureCadMapForHandle();
      if (repairGen !== applyGeneration || !isCurrentFormVariant(variant)) return;
      targetCad = cadForVariant(variant);
      onStage = modelRootChildren().length > 0;
    }

    // Still no cad → this variant genuinely has no 3D model (no cad_code).
    if (!targetCad) {
      if (currentCad != null && onStage) {
        setStaleState(variant, '', 'variant has no custom.cad_code');
        return;
      }
      currentCad = null;
      clearModel();
      setState('empty');
      return;
    }

    // Same geometry as what is staged → materials/config only. No resolution,
    // no network, no GLB reload. This is the Stone Type path, and the
    // combined-listing metal switch (metal siblings share cad codes).
    if (targetCad === currentCad && onStage) {
      clearStaleState();
      await applyConfigOnStagedGeometry(variant);
      setState('ready');
      return;
    }

    // Geometry change (or an empty stage) → we need this cad's GLB url.
    var entry = variantMap[String(variant.id)];
    var url = entry && entry.url;
    var resolver = window.IjewelCadResolver;
    if (!url && resolver) {
      var known = resolver.peek(targetCad);
      if (known) url = known.url;
    }

    if (!url) {
      if (!resolver) {
        if (onStage) setStaleState(variant, targetCad, 'CAD resolver unavailable');
        else setState('empty');
        return;
      }
      // Capture the generation BEFORE awaiting. scheduleApplyVariant() bumps
      // applyGeneration per selection, so if the shopper picks something else
      // while this resolve is in flight, the check after the await drops this
      // (slower, older) response instead of letting it overwrite the newer
      // model. loadToken is taken only AFTER resolution, so a losing resolve
      // never supersedes the winning swap.
      var gen = applyGeneration;
      if (!(currentCad != null && onStage)) setState('loading');
      try {
        var resolved = await resolver.resolve(targetCad, variant.id);
        url = resolved && resolved.url;
      } catch (error) {
        // The resolver already logged variant/cad/endpoint/reason.
        if (gen !== applyGeneration) return;
        if (modelRootChildren().length > 0) {
          setStaleState(variant, targetCad, 'CAD resolution failed');
        } else {
          setState('empty');
        }
        return;
      }
      if (gen !== applyGeneration || !isCurrentFormVariant(variant)) return;
      syncVariantMap();
      onStage = modelRootChildren().length > 0;
      if (!url) {
        if (onStage) setStaleState(variant, targetCad, 'resolver returned no url');
        else setState('empty');
        return;
      }
    }

    var token = ++loadToken;

    // Geometry change OR stage was cleared (e.g. a racing variant:update hit the
    // no-entry path above) → restore from cache / fetch. Same-cad metal switches
    // normally skip this, but an empty stage must always be rebuilt.
    if (targetCad !== currentCad || !onStage) {
      // Preserve the shopper's current camera across a shape swap: the new GLB
      // loads with its own exported camera, which would otherwise snap the view
      // back and discard whatever orbit/zoom the user set. Only after the first
      // model is up — the initial load has no prior pose and uses the exported
      // camera as-is (GLBs are delivered pre-framed; no auto-fit).
      var savedPose = firstModelReady ? captureCameraPose() : null;

      if (!(currentCad != null && onStage)) setState('loading');
      try {
        await swapModel(url, targetCad, token);
      } catch (error) {
        console.error('iJewel 3D: model load failed', error);
        // Keep the current model on a failed swap: importModel no longer clears
        // the scene on failure, so the prior geometry is still on stage. Only
        // fall back to the empty state if nothing is actually rendered.
        if (token === loadToken) {
          if (modelRootChildren().length > 0) {
            setStaleState(variant, targetCad, 'GLB load/parse failed');
          } else {
            setState('empty');
          }
        }
        return;
      }
      if (token !== loadToken) return; // a newer selection superseded this one
      // INVARIANT: internal state must never claim a cad is loaded while the
      // scene is empty. swapModel() resolving is not proof of that — verify the
      // stage before recording it. If currentCad were set here over an empty
      // stage, the same-cad short-circuit at the top of applyVariant would match
      // on the next selection and skip the reload, leaving the viewer blank for
      // good (only a DIFFERENT shape could recover it).
      if (modelRootChildren().length === 0) {
        setState('empty');
        console.warn('iJewel 3D: swap completed with nothing staged', {
          variantId: variant.id,
          wantedCad: targetCad,
          stagedCad: currentCad || '(none)',
        });
        return;
      }
      currentCad = targetCad;
      if (savedPose) restoreCameraPose(savedPose);
    }

    clearStaleState();
    // On first load, stageFirstLoad defers ready to the settle window — but ONLY
    // for the swap that opened it. When a shopper changes shape while the initial
    // model is still importing, that first load's reveal is still pending when
    // this (newer) swap finishes; deferring to it would hand the reveal to a
    // callback that then bails out as superseded, leaving the viewer stuck at
    // opacity 0 until the next option change. Ownership check, not a bare flag.
    // READY means "geometry is on stage" — the guard above already returns on an
    // empty one, so the count here is a final assertion, not a second path.
    var deferReveal = stageRevealPending && stageRevealToken === token;
    if (!deferReveal && modelRootChildren().length > 0) {
      // A superseded first-load still owns the pending flag until its late
      // reveal fires — clear it here so idle pre-stage / later applies don't
      // keep treating a dead window as active.
      if (stageRevealPending && stageRevealToken !== token) {
        stageRevealPending = false;
        stageRevealToken = -1;
      }
      setState('ready');
    }
    // Gem/metal applyVariation can rebuild diamond materials and clear Bloom.
    // Re-bake facets against the post-variation materials, then re-assert
    // desktop bloom so white diamonds don't settle milky/frosted.
    await applyConfigOnStagedGeometry(variant);
    if (modelRootChildren().length > 0) notifyFirstModelReady();
  }

  /* ---- viewer state (spinner / empty) ------------------------------------ */

  function setState(state) {
    if (!root) return;
    root.classList.toggle('is-loading', state === 'loading');
    root.classList.toggle('is-empty', state === 'empty');
    if (loadingEl) loadingEl.hidden = state !== 'loading';
    if (emptyEl) emptyEl.hidden = state !== 'empty';
  }

  function modelRootChildren() {
    var viewer = window.iJewelActiveViewer;
    if (viewer && viewer.scene && viewer.scene.modelRoot) {
      return viewer.scene.modelRoot.children.slice();
    }
    return [];
  }

  function clearModel() {
    var viewer = window.iJewelActiveViewer;
    if (viewer && viewer.scene && viewer.scene.modelRoot) {
      viewer.scene.modelRoot.children.slice().forEach(function (model) {
        viewer.scene.modelRoot.remove(model);
      });
    }
  }

  /* ---- model swapping ------------------------------------------------------
   *
   * Zero-loading strategy:
   *   cached cad   → stash the visible objects, re-add the cached ones in the
   *                  same frame. No fetch, no parse.
   *   uncached cad → importSinglePath() parses OFF the scene while the old
   *                  model stays visible, then the swap is a single re-add.
   *                  (HTTP cache is already warm via product-ijewel-prefetch.)
   * Removed objects are kept (not disposed) in `modelCache` so returning to a
   * shape is instant; the LRU cap bounds GPU memory.
   */

  async function swapModel(url, cad, token) {
    var viewer = window.iJewelActiveViewer;
    if (!viewer || !url) return;
    if (isBridgeSuppressed() || token !== loadToken) return;
    dispatchSwapEvent('ijewel:model-swap-start', { cad: cad, url: url, token: token });

    var objects = modelCache[cad];
    var fromCache = !!(objects && objects.length);
    if (!fromCache) {
      var imported = await importModel(url);
      if (isBridgeSuppressed() || token !== loadToken) {
        // A newer swap bumped loadToken (or the test panel took over) while this
        // GLB was parsing. `imported` was never added to the scene or cached, so
        // GC can't reclaim its three.js geometry/textures — dispose it explicitly.
        // Without this, rapid metal/shape switching leaks one full model per
        // superseded swap, a real path toward the iOS WebGL memory ceiling.
        disposeModel(imported);
        return;
      }
      objects = imported;
      cacheModel(cad, objects);
    }
    if (isBridgeSuppressed() || token !== loadToken) return;
    touchCache(cad);

    // A first-stage add needs a settle window before it is revealed. Two things
    // resolve over the frames AFTER the swap and must not be shown mid-flight:
    //   1. Gem crispness. prepareDiamondMeshes re-bakes the facet normal-maps
    //      (see the guard there), then applyConfigFromOptions applies the metal
    //      and gem variations — each dirties the scene, resetting the engine's
    //      progressive AA/reflection accumulation (renderManager._frameCount →
    //      Progressive.maxFrameCount). Reveal too early and the shopper watches
    //      a soft, low-contrast diamond sharpen into place — on white diamonds
    //      this reads as slow and can be mistaken for the milkiness bug.
    //      Measured: gem-facet contrast (luminance stddev over the gem row)
    //      climbs from ~31 at first paint to ~38 once accumulation settles.
    //   2. HISTORICAL (WebGi 0.15): the first ADD presented one corrupt dark-navy
    //      frame ~14-16 frames later. That "blue flash" is GONE on the 0.22
    //      (Threepipe) engine — verified by sampling the WebGL back buffer at
    //      every postFrame across a first-stage add (corners held background
    //      cream, zero navy frames) — so the mask is no longer needed for it, but
    //      the convergence wait in (1) still is.
    // So: snapshot the clean prior frame and hold it through the settle window
    // (scheduleUnfreeze). The very first load has no prior frame — keep the
    // canvas hidden via is-loading until the same window elapses. A re-stage of
    // an already-baked, already-converged cad only needs the short quick window.
    var firstStage = !stagedCads[cad];
    // Cold swap + SDK loading screen registered: the screen (monogram +
    // progress, shown via ijewel:model-swap-start above) owns the visible
    // loading treatment for this wait. The freeze keeps ONLY its functional
    // role — a static last-frame mask over staging + convergence — with the
    // shimmer dropped so two loading treatments never run over one swap.
    // Cache-hit swaps import nothing, show no screen, and keep the shimmer.
    var screenCovers = !fromCache && window.__ijewelSwapLoadingScreen === true;
    var unfreeze = null;
    var useQuickUnfreeze = false;
    if (firstStage) {
      unfreeze =
        modelRootChildren().length > 0
          ? freezeCanvas(viewer, { noShimmer: screenCovers })
          : stageFirstLoad();
    } else {
      unfreeze = freezeCanvas(viewer, { noShimmer: screenCovers });
      useQuickUnfreeze = !!unfreeze;
    }

    try {
      await swapModelStaged(viewer, objects, firstStage, token);
      if (token === loadToken) {
        dispatchSwapEvent('ijewel:model-swap-staged', { cad: cad, url: url, token: token });
      }
      if (token === loadToken) stagedCads[cad] = true;
    } finally {
      if (unfreeze) {
        if (useQuickUnfreeze) scheduleQuickUnfreeze(viewer, unfreeze);
        else scheduleUnfreeze(viewer, unfreeze);
      }
    }
    if (token === loadToken) {
      dispatchSwapEvent('ijewel:model-swap-visible', { cad: cad, url: url, token: token });
      scheduleSwapDoneEvent(viewer, { cad: cad, url: url, token: token });
    }
    setDirty(viewer);
  }

  function dispatchSwapEvent(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (_e) {
      /* best effort */
    }
  }

  function scheduleSwapDoneEvent(viewer, detail) {
    var fired = false;
    function done() {
      if (fired) return;
      fired = true;
      try {
        viewer.removeEventListener('postFrame', onFrame);
      } catch (_e) {
        /* listener already gone */
      }
      dispatchSwapEvent('ijewel:model-swap-done', detail);
    }
    var frames = 0;
    function onFrame() {
      frames++;
      if (frames >= 2) done();
    }
    try {
      viewer.addEventListener('postFrame', onFrame);
      setDirty(viewer);
    } catch (_e) {
      done();
      return;
    }
    setTimeout(done, 300);
  }

  function scheduleQuickUnfreeze(viewer, unfreeze) {
    var start = Date.now();
    var frames = 0;
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      try {
        viewer.removeEventListener('postFrame', onFrame);
      } catch (_e) {
        /* listener already gone */
      }
      unfreeze();
    }
    function onFrame() {
      frames++;
      if (frames >= QUICK_REVEAL_MIN_FRAMES && Date.now() - start >= QUICK_REVEAL_MIN_MS) finish();
      else setDirty(viewer);
    }
    try {
      viewer.addEventListener('postFrame', onFrame);
      setDirty(viewer);
    } catch (_e) {
      finish();
      return;
    }
    setTimeout(finish, QUICK_REVEAL_TIMEOUT_MS);
  }

  /* Overlay a bitmap copy of the canvas's current (clean) contents over the
     viewer, with a token-driven shimmer (product-ijewel-viewer.liquid CSS).
     Returns a remover. Best effort — on any failure the swap just runs
     unmasked, same as before this guard.

     opts.noShimmer keeps the snapshot (the functional mask over the staging /
     convergence window) but drops the shimmer — the VISIBLE loading treatment.
     Used on cold swaps, where the SDK loading screen (monogram + progress,
     product-model-ijewel.js) already owns the loading UI: running its screen
     and this shimmer together is two competing treatments over one wait. */
  function freezeCanvas(viewer, opts) {
    try {
      var canvas = viewer.canvas || document.querySelector('.ijewel-viewer-container canvas');
      if (!canvas || !canvas.parentElement) return null;
      var host = document.createElement('div');
      host.className = 'ijewel-freeze';
      host.setAttribute('aria-hidden', 'true');

      var snap = document.createElement('canvas');
      snap.className = 'ijewel-freeze__snap';
      snap.width = canvas.width;
      snap.height = canvas.height;
      snap.getContext('2d').drawImage(canvas, 0, 0);
      snap.setAttribute('aria-hidden', 'true');

      host.appendChild(snap);
      if (!(opts && opts.noShimmer)) {
        var shimmer = document.createElement('span');
        shimmer.className = 'ijewel-freeze__shimmer';
        shimmer.setAttribute('aria-hidden', 'true');
        host.appendChild(shimmer);
      }
      canvas.parentElement.appendChild(host);
      return function () {
        host.remove();
      };
    } catch (_e) {
      return null;
    }
  }

  /* First load has no clean prior frame to snapshot, and a hardware-accelerated
     WebGL canvas composites ABOVE DOM overlays — so the canvas is hidden via
     CSS for the whole `.is-loading` phase (product-ijewel-viewer.liquid): the
     cream background + spinner show from first paint, through boot AND the
     first-add corrupt frame. Here we only hold `loading` until the settle
     window has rendered through; the returned remover reveals the clean model. */
  function stageFirstLoad() {
    // The swap that opened this reveal window. swapModel() schedules the reveal
    // from a `finally`, so it runs even when staging THREW — and it fires on a
    // frame/timer long after the swap returned, by which point a newer selection
    // may own the stage. Both cases must be re-checked at reveal time.
    var revealToken = loadToken;
    stageRevealPending = true;
    stageRevealToken = revealToken;
    return function () {
      // Only release the flag if it is still OURS — a later first-load swap may
      // have opened its own window, and clearing that one would reveal it early.
      if (stageRevealToken === revealToken) {
        stageRevealPending = false;
        stageRevealToken = -1;
      }
      // Superseded: the newer swap owns the viewer state, leave it alone. It has
      // already declared its own ready (applyVariant skips the deferral when the
      // pending reveal is not its own), so returning here cannot strand
      // `is-loading`.
      if (revealToken !== loadToken) return;
      // INVARIANT: never reveal `ready` over an empty stage.
      setState(modelRootChildren().length > 0 ? 'ready' : 'empty');
    };
  }

  /* Lift the freeze only after the corrupt-frame window has RENDERED through:
     at least UNFREEZE_MIN_FRAMES postFrame ticks (the corruption lands ~14-16
     frames after the add) and UNFREEZE_MIN_MS wall time, with a hard timeout
     in case the on-demand render loop goes idle early. */
  function scheduleUnfreeze(viewer, unfreeze) {
    var start = Date.now();
    var frames = 0;
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      try {
        viewer.removeEventListener('postFrame', onFrame);
      } catch (_e) {
        /* listener already gone */
      }
      unfreeze();
    }
    function onFrame() {
      frames++;
      if (frames >= UNFREEZE_MIN_FRAMES && Date.now() - start >= UNFREEZE_MIN_MS) finish();
      else setDirty(viewer); // keep the on-demand loop ticking through the window
    }
    try {
      viewer.addEventListener('postFrame', onFrame);
      setDirty(viewer);
    } catch (_e) {
      /* no frame events — fall through to the timeout */
    }
    setTimeout(finish, UNFREEZE_TIMEOUT_MS);
  }

  function waitSettleWindow(viewer, unfreeze) {
    return new Promise(function (resolve) {
      scheduleUnfreeze(viewer, function () {
        if (unfreeze) unfreeze();
        resolve();
      });
    });
  }

  async function swapModelStaged(viewer, objects, firstStage, token) {
    if (isBridgeSuppressed() || token !== loadToken) return;
    await runWithInstantTransitions(viewer, async function () {
      // Add the new model INVISIBLE alongside the old one, force its
      // shaders/textures to compile+upload on the GPU while hidden, then flip
      // visibility and remove the old model in one step. A naive clear-then-add
      // shows the new mesh mid-upload — an unlit "default material" flash
      // (commonly a flat blue-ish sheen) before its real PBR look kicks in.
      //
      // addSceneObject() is an SDK call, not a raw three.js .add() — don't
      // assume the objects we hold are the same references it inserts into
      // modelRoot. Diff the actual children before/after each add instead.
      var stale = modelRootChildren();
      var added = [];
      // The cad that owns `stale` — needed when DISABLE_MODEL_CACHE disposes
      // the outgoing model below (currentCad has not been reassigned yet here,
      // but capture it explicitly so the disposal tail can't drift).
      var outgoingCad = currentCad;
      // With the group's cache disabled, cacheModel() stores nothing — once the
      // outgoing objects are off stage nothing owns them, so they must be
      // disposed here or every swap leaks a full model. Deferred past the
      // rollback window (the !added.length throw re-adds `stale`).
      function disposeStaleIfUncached() {
        if (!DISABLE_MODEL_CACHE || !stale.length) return;
        // A disposed model's revisit re-imports fresh meshes that have never
        // been through prepareDiamondMeshes — clear the staged flag so that
        // reimport is treated as a genuine firstStage (same reason LRU
        // eviction clears it in cacheModel).
        if (outgoingCad) delete stagedCads[outgoingCad];
        disposeModel(stale);
      }

      /* MOBILE: free the outgoing model BEFORE staging the incoming one.
         Desktop keeps add-then-remove.

         The add-alongside order above exists to hide an unlit "default
         material" flash while the new mesh uploads. But it means both models
         are resident at once, and on the heaviest pieces that is what kills
         the tab: the bezel graduated tennis necklace is 1,679,710 triangles
         across 528 meshes, so a carat change briefly holds ~3.4M triangles.
         That is why it crashes on a SWAP rather than on first load.

         Removing first roughly halves peak memory at exactly that moment. The
         flash it was protecting against is already covered on this path —
         freezeCanvas() holds a snapshot of the last clean frame with a shimmer
         over it for the whole settle window, so there is nothing visible to
         protect.

         Safer than it looks: `objects` are already downloaded and parsed by
         the time this runs, so the network and decode failures have already
         happened. Only the SDK's addToScene can still no-op — and the
         invariant below rolls the outgoing model back in if it does, so the
         stage is never left blank. */
      var freeFirst = IS_MOBILE && stale.length > 0;
      if (freeFirst) {
        // Mirrors the tail-end removal: cache (a no-op on mobile, where the
        // cache size is 0) then detach. Never dispose — cached models may
        // still own these objects.
        if (currentCad) cacheModel(currentCad, stale);
        stale.forEach(function (child) {
          viewer.scene.modelRoot.remove(child);
        });
      }

      for (var i = 0; i < objects.length; i++) {
        var beforeOne = modelRootChildren();
        await addToScene(viewer, objects[i]);
        modelRootChildren().forEach(function (child) {
          if (beforeOne.indexOf(child) === -1 && stale.indexOf(child) === -1 && added.indexOf(child) === -1) {
            child.visible = false;
            added.push(child);
          }
        });
      }

      // INVARIANT: never remove the currently-visible model unless its
      // replacement actually reached the scene. addToScene() is an SDK call and
      // can no-op without rejecting (and importSinglePath can hand back an empty
      // set), in which case the removal below would blank the stage and the
      // caller would go on to declare `ready` over an empty scene — the blank
      // viewer bug. Throwing here instead routes through applyVariant's catch,
      // which keeps the prior geometry up and marks it stale. Skip the throw when
      // superseded: the token block just below owns that case.
      if (!added.length && token === loadToken) {
        /* On the mobile remove-first path the outgoing model is already
           detached, so "keeping previous model" is not automatic — put it
           back before throwing, or applyVariant's catch would leave an empty
           stage and the blank-viewer bug returns. Re-adding the same object
           references we removed moments ago; they were never disposed. */
        if (freeFirst) {
          stale.forEach(function (child) {
            viewer.scene.modelRoot.add(child);
            child.visible = true;
          });
        }
        throw new Error('iJewel 3D: scene add produced no objects — keeping previous model');
      }

      if (firstStage && token === loadToken) {
        // Explicitly prepare gem meshes BEFORE reveal: DiamondPlugin's
        // auto-prepare skips invisible meshes (WebGi 0.12.3+), so hidden-add
        // would otherwise defer preparation to the first visible frame.
        // prepareDiamondMesh is synchronous + idempotent (verified live);
        // params come from the loader's own userData stash.
        prepareDiamondMeshes(viewer, added);
        await precompile(viewer, added);
      }
      if (token !== loadToken) {
        // Superseded after the invisible add but before the stale removal:
        // pull the just-added (still invisible) objects back out of the scene,
        // else they linger as orphans in modelRoot and the NEXT swap caches
        // them under the wrong cad. Remove-from-scene only — these objects may
        // already live in modelCache under their own cad, so never dispose.
        added.forEach(function (child) {
          viewer.scene.modelRoot.remove(child);
        });
        // Cache-disabled + mobile remove-first: `stale` was detached above and
        // (cacheModel being a no-op) never stored — the newer swap re-imports
        // from scratch, so these objects are unreachable. Free them. On the
        // desktop path stale is still ON stage here (its removal comes later),
        // so it must be left alone.
        if (freeFirst) disposeStaleIfUncached();
        return;
      }

      // Skipped on the mobile remove-first path — the outgoing model was
      // cached and detached before the add, so there is nothing left to do.
      if (!freeFirst) {
        if (currentCad && stale.length) cacheModel(currentCad, stale);
        stale.forEach(function (child) {
          viewer.scene.modelRoot.remove(child);
        });
      }
      added.forEach(function (child) {
        child.visible = true;
      });
      // Both paths have the outgoing model off stage and uncached by now, and
      // the rollback window is over — safe point to free it for cache-disabled
      // groups. No-op when the cache is on (objects live in modelCache).
      disposeStaleIfUncached();
    });
  }

  /* Force the renderer to compile shaders + upload textures BEFORE the new
     model is shown — this is what prevents the unlit "blue flash". Compiles
     only the freshly added objects when given (three.js accepts any Object3D
     as compile()'s first arg, with the scene passed as targetScene for
     lights/env — and compile ignores `visible: false`, which is why the
     invisible-add pattern works); compiling the whole scene on every swap
     re-walks every material and made cache-miss swaps noticeably slower.
     Best-effort: if the SDK's internal renderer isn't reachable, the swap
     proceeds without the flash guard. */
  async function precompile(viewer, addedObjects) {
    try {
      var renderer = viewer.renderManager && viewer.renderManager._renderer;
      var scene = viewer.scene;
      var camera = scene && (scene._activeCamera || scene.defaultCamera);
      if (!renderer || !scene || !camera) return;

      var targets = addedObjects && addedObjects.length ? addedObjects : [scene];
      for (var i = 0; i < targets.length; i++) {
        if (typeof renderer.compileAsync === 'function') {
          await renderer.compileAsync(targets[i], camera, scene);
        } else if (typeof renderer.compile === 'function') {
          renderer.compile(targets[i], camera, scene);
        }
      }
    } catch (_e) {
      /* best effort — the swap proceeds without precompilation */
    }
  }

  // Monotonic id so each loaded model gets a distinct diamond cacheKey.
  var diamondBakeSeq = 0;

  /* Force DiamondPlugin preparation on every gem mesh in the given roots.
     Auto-prepare ignores invisible meshes, and our swap adds models hidden —
     without this call preparation lands on the reveal frame. normalMapRes comes
     from the mesh userData the iJewel loader stashes at parse time. Sync +
     idempotent; best effort.

     Milkiness fix: DiamondPlugin bakes the facet-sparkle normal-map into
     _normalCapture._normalsCache, keyed on segments of `cacheKey;geometry.uuid`,
     and REUSES a cached target if ANY segment already exists. A UNIQUE cacheKey
     per load is NOT sufficient on the 0.22 engine: the normal-map bakes all share
     the plugin's single capture mesh/scene + framebuffer, and leaving a previous
     shape's render targets allocated pollutes the next shape's capture — the new
     bake reads back a flat, frosted map with no facet contrast ("milky"). This is
     a STALE-TARGET problem, not only a cache-key-collision one. Verified on 0.22:
     with the stone set to White Lab Diamond (milkiness is nearly invisible on
     dark sapphires — do NOT verify gem clarity on coloured stones), swapping
     Emerald → Pear leaves Pear's diamonds flat/frosted; inspecting the cache in
     that state shows ~17 keys collapsed onto a SINGLE surviving render target.
     _normalCapture.disposeAllTargets() (disposeAllCacheMaps is a no-op while
     geometries stay attached) is the only reliable fix — it disposes every target
     and empties _normalsCache so each shape re-bakes into a clean framebuffer.

     Because disposeAllTargets() is GLOBAL (wipes every previously-baked shape's
     targets, not just the one being staged), stagedCads can't be trusted
     afterward for any OTHER cad either — invalidate it wholesale here so
     swapModelStaged's `firstStage = !stagedCads[cad]` check treats a later revisit
     to ANY shape as a genuine first stage and re-bakes it, instead of skipping and
     leaving it stuck with the now-disposed target ("cloudy on revisit"). Eviction
     also clears stagedCads[cad] in cacheModel() for the same reason. Do NOT rename
     mat.name/ext.name to freshKey: the configurator matches meshes to their
     gem/metal category by testing the category's regex against the material's
     CURRENT name (e.g. "Gem 01"), and overwriting it permanently detaches the
     mesh so gem/metal swaps silently stop applying. Drop stale
     geometry.userData._normalsCaptureMap so hasCapturedNormalMap can't
     short-circuit the fresh bake. */
  function prepareDiamondMeshes(viewer, objects) {
    try {
      var dp = viewer.getPluginByType && viewer.getPluginByType('Diamond');
      if (!dp || typeof dp.prepareDiamondMesh !== 'function') return;
      var nc = dp._normalCapture;
      if (nc && typeof nc.disposeAllTargets === 'function') {
        nc.disposeAllTargets();
        // Every other cad's bake was just wiped along with this one's — clear the
        // whole map so a later revisit is treated as firstStage again. The current
        // cad's flag is re-set to true by the caller once this stage completes.
        for (var staleCad in stagedCads) delete stagedCads[staleCad];
      }
      var freshKeyBase = 'ijewel-bake-' + diamondBakeSeq++;
      var meshIdx = 0;
      objects.forEach(function (root) {
        if (typeof root.traverse !== 'function') return;
        root.traverse(function (node) {
          if (!node.isMesh || !node.material) return;
          var ud = node.userData || (node.userData = {});
          if (!node.material.isDiamondMaterial && ud._diamondCacheKey == null) return;
          // Per-mesh key: a shared batch key makes DiamondPlugin alias every
          // stone onto one render target (last bake wins) — multi-stone rings
          // then read milky through a single collapsed map.
          var freshKey = freshKeyBase + '-' + meshIdx++;
          ud._diamondCacheKey = freshKey;
          if (node.geometry && node.geometry.userData) {
            delete node.geometry.userData._normalsCaptureMap;
          }
          var params = { cacheKey: freshKey };
          if (ud._diamondNormalMapRes != null) params.normalMapRes = ud._diamondNormalMapRes;
          dp.prepareDiamondMesh(node, params);
        });
      });
    } catch (_e) {
      /* best effort — swap proceeds; auto-prepare covers it at reveal */
    }
  }

  /* Parse a GLB OFF the scene via the importer. webgi 0.15 always exposes
     importSinglePath; there is no viewer.load(url) fallback — that legacy path
     clears+adds on the LIVE scene, so a background pre-parse (drainPreParse)
     could drop a model onto the visible stage. Missing importer → reject, so
     swapModel's caller keeps the current model up rather than blanking. */
  async function importModel(url) {
    url = normalizeAssetUrl(url);
    var viewer = window.iJewelActiveViewer;
    var importer =
      (viewer.getManager && viewer.getManager() && viewer.getManager().importer) || null;

    if (!importer || typeof importer.importSinglePath !== 'function') {
      throw new Error('iJewel 3D: importSinglePath unavailable — cannot import ' + url);
    }

    var result = await importer.importSinglePath(url);
    if (result) {
      var objects = Array.isArray(result) ? result : [result];
      // An empty array is truthy — importSinglePath can resolve to `[]` (GLB
      // fetched but yielded zero scene objects: CDN glitch, partial response).
      // That must be treated the same as "yielded nothing" below, or
      // swapModelStaged happily removes the OLD model and adds nothing, and
      // applyVariant declares `ready` over an empty stage (see setState call).
      if (objects.length) return objects;
    }
    // Importer present but yielded nothing — e.g. a GLB fetch that failed
    // (network drop / 5xx). Throw so swapModel never touches the scene and
    // applyVariant's catch leaves the current model up.
    throw new Error('iJewel 3D: import returned no objects: ' + url);
  }

  async function addToScene(viewer, object) {
    if (viewer.scene && typeof viewer.scene.addSceneObject === 'function') {
      await viewer.scene.addSceneObject(object);
    } else if (viewer.scene && viewer.scene.modelRoot) {
      viewer.scene.modelRoot.add(object);
    }
  }

  function setDirty(viewer) {
    if (typeof viewer.setDirty === 'function') viewer.setDirty();
    else if (viewer.scene && typeof viewer.scene.setDirty === 'function') viewer.scene.setDirty();
  }

  function cacheModel(cad, objects) {
    if (!cad || !objects || !objects.length) return;
    // Cache disabled for this Product Group: store nothing. Callers whose
    // objects end up off-stage handle their own disposal (swapModelStaged's
    // tail, drainPreParse's else-branch) — cacheModel must never dispose,
    // because swapModel also calls it for the INCOMING model right before
    // staging it.
    if (DISABLE_MODEL_CACHE) return;
    modelCache[cad] = objects;
    touchCache(cad);
    while (modelCacheOrder.length > MODEL_CACHE_LIMIT) {
      var oldest = modelCacheOrder.shift();
      if (oldest === currentCad) {
        modelCacheOrder.push(oldest); // never evict what's on stage
        continue;
      }
      disposeModel(modelCache[oldest]);
      delete modelCache[oldest];
      // Evicted objects are gone for good; a later revisit re-imports fresh
      // mesh/material instances that have never been through
      // prepareDiamondMeshes. Clear the stale flag so swapModelStaged treats
      // that reimport as a genuine firstStage again — otherwise the new gem
      // meshes never get their diamond normal-map baked (cloudy forever).
      delete stagedCads[oldest];
    }
  }

  function touchCache(cad) {
    if (DISABLE_MODEL_CACHE) return; // no LRU book-keeping when nothing is stored
    var idx = modelCacheOrder.indexOf(cad);
    if (idx !== -1) modelCacheOrder.splice(idx, 1);
    modelCacheOrder.push(cad);
  }

  function disposeModel(objects) {
    (objects || []).forEach(function (object) {
      try {
        if (typeof object.dispose === 'function') object.dispose();
        else if (typeof object.traverse === 'function') {
          object.traverse(function (node) {
            if (node.geometry && node.geometry.dispose) node.geometry.dispose();
            var materials = Array.isArray(node.material) ? node.material : [node.material];
            materials.forEach(function (material) {
              if (material && material.dispose) material.dispose();
            });
          });
        }
      } catch (_e) {
        /* best effort */
      }
    });
  }

  /* ---- background pre-parse ------------------------------------------------
   * product-ijewel-prefetch.js announces each network-warmed GLB. Parsing it
   * here (off the scene, during idle time) upgrades the next shape change
   * from "parse ~400ms" to a same-frame toggle. */

  function schedulePreParse(url, options) {
    if (!url) return;
    if (preParseSeen[url] || preParseQueue.indexOf(url) !== -1) {
      preParseTelemetry.skippedSeen++;
      publishPreParseTelemetry();
      return;
    }
    var priority = !!(options && options.priority);
    if (priority) preParseQueue.unshift(url);
    else preParseQueue.push(url);
    preParseTelemetry.queued++;
    if (priority) preParseTelemetry.prioritizedQueued++;
    publishPreParseTelemetry();
    drainPreParse();
  }

  function drainPreParse() {
    if (preParseBusy || !viewerReady) return;
    var url = preParseQueue.shift();
    if (!url) return;

    var cad = cadForUrl(url);
    if (!cad || modelCache[cad] || cad === currentCad) {
      preParseSeen[url] = true;
      preParseTelemetry.skippedCached++;
      publishPreParseTelemetry();
      drainPreParse();
      return;
    }

    preParseBusy = true;
    var idle = window.requestIdleCallback || function (fn) { setTimeout(fn, 200); };
    idle(function () {
      importModel(url)
        .then(function (objects) {
          preParseSeen[url] = true;
          // Cache only if this cad isn't already the staged one and the viewer
          // is still alive. Otherwise the freshly-parsed objects are orphaned
          // (never added, never cached) and would leak — dispose them instead.
          if (!DISABLE_MODEL_CACHE && cad !== currentCad && viewerReady) cacheModel(cad, objects);
          else disposeModel(objects);
          preParseTelemetry.parsed++;
          publishPreParseTelemetry();
          schedulePreStageDrain();
        })
        .catch(function () {
          delete preParseSeen[url];
          preParseTelemetry.failed++;
          publishPreParseTelemetry();
        })
        .then(function () {
          preParseBusy = false;
          drainPreParse();
        });
    }, { timeout: 3000 });
  }

  function cadForUrl(url) {
    var map = variantMap || {};
    var ids = Object.keys(map);
    for (var i = 0; i < ids.length; i++) {
      var entry = map[ids[i]];
      if (entry && entry.url === url) return entry.cad;
    }
    return null;
  }

  function publishPreParseTelemetry() {
    window.iJewelPreParseTelemetry = {
      cap: preParseTelemetry.cap,
      queued: preParseTelemetry.queued,
      prioritizedQueued: preParseTelemetry.prioritizedQueued,
      skippedSeen: preParseTelemetry.skippedSeen,
      skippedCached: preParseTelemetry.skippedCached,
      parsed: preParseTelemetry.parsed,
      failed: preParseTelemetry.failed,
      pending: preParseQueue.length,
      inFlight: preParseBusy,
      cacheSize: modelCacheOrder.length,
    };
  }

  /* Disable FrameFade during a scripted update so variant-driven swaps don't
     cross-fade; restore afterward. */
  async function runWithInstantTransitions(viewer, fn) {
    var frameFade = viewer.getPluginByType && viewer.getPluginByType('FrameFade');
    var prevMaterial;
    var prevScene;

    if (frameFade) {
      prevMaterial = frameFade.fadeOnMaterialUpdate;
      prevScene = frameFade.fadeOnSceneUpdate;
      frameFade.fadeOnMaterialUpdate = false;
      frameFade.fadeOnSceneUpdate = false;
      if (typeof frameFade.stopTransition === 'function') frameFade.stopTransition();
    }

    try {
      await fn();
    } finally {
      if (frameFade) {
        frameFade.fadeOnMaterialUpdate = prevMaterial;
        frameFade.fadeOnSceneUpdate = prevScene;
      }
    }
  }

  /* ---- configuration (from product options) ------------------------------
   *
   * The GLB's variation CATEGORIES are named generically ("Metal 01", "Gem 01",
   * "Metal 02") and do NOT match the Shopify option names ("Stone Type",
   * "Stone Shape", "Stone Shape : Secondary Metal : Length"). The authored
   * OPTION values, however, align 1:1 with the Shopify values ("White Lab
   * Diamond", "14K Rose Gold", ...). Two mapping rules — both driven by the data
   * conventions, not hardcoded product strings:
   *
   *   Gems (and any single-slot category): matched by VALUE. Each variant option
   *   value is applied to every NON-metal category that offers it — "Zambian Lab
   *   Emerald" lands in "Gem 01"; shape/size values match nothing and no-op.
   *
   *   Metals: matched by SLOT ORDINAL. A ring/bracelet may have one or two metal
   *   categories ("Metal 01", "Metal 02"). Value matching can't tell them apart
   *   (both list the same metal names), so we order the metal categories by
   *   their trailing number and fill them from the ordered metal SOURCES:
   *     1. primary   = the product-level metal (combined listing → from title),
   *     2. secondary = the value of the compound option part whose sub-name
   *        contains "metal" (e.g. "Stone Shape : Secondary Metal : Length"),
   *        else the SECOND metal named in the title (two-tone listings, e.g.
   *        "… - 9K Yellow Gold & 9K White Gold" → Metal 02 ← 9K White Gold).
   *   Any slot left over after those sources falls back to the PRIMARY metal, so
   *   a single-tone piece renders BOTH configurable slots in its own metal
   *   instead of keeping the GLB's authored default (that default is why
   *   "Bezel Solitaire Bracelet - 9K Yellow Gold" showed Metal 02 = 14K Yellow
   *   Gold). Slots whose secondary material is intentionally FIXED are protected
   *   by option matching, not by a product list: Georgian's "Metal 02" exposes
   *   only "White Rhodium", so the fallback finds no matching option there and
   *   the authored value is left untouched. Rings with one slot just fill
   *   Metal 01 and stop.
   */

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\s_\-:]+/g, '')
      .trim();
  }

  async function applyConfigFromOptions(variant) {
    var plugin = getVariationPlugin();
    if (!plugin) return; // GLB has no authored variations → nothing to apply

    var categories = getCategories(plugin);
    if (!categories.length) return;

    var metalCats = categories
      .filter(isMetalCategory)
      .sort(function (a, b) {
        return categoryOrdinal(a) - categoryOrdinal(b);
      });
    var otherCats = categories.filter(function (c) {
      return !isMetalCategory(c);
    });

    // Metals → slots by ordinal: primary → Metal 01, secondary → Metal 02, …
    // Slots past the last known source fall back to the primary metal; findOption
    // is the guard that leaves intentionally-fixed slots alone (see block above).
    var metalValues = collectMetalValues(variant, categories);
    var primaryMetal = metalValues[0] || '';
    for (var m = 0; m < metalCats.length; m++) {
      var wantedMetal = metalValues[m] || primaryMetal;
      if (!wantedMetal) continue;
      var metalOption = findOption(metalCats[m], wantedMetal);
      if (metalOption) await applyOption(plugin, metalCats[m], metalOption);
    }

    // Gems, shapes, and other authored values — split compound options into
    // their parts so "Round" / "Pear (NS)" apply even when bundled in option4.
    var configValues = collectConfigValues(variant);
    for (var c = 0; c < configValues.length; c++) {
      await applyValueInCategories(plugin, otherCats, configValues[c]);
    }
  }

  function isMetalCategory(category) {
    return /metal/i.test(category.title || category.name || category.uuid || '');
  }

  // Trailing number in the category label ("Metal 01" → 1, "Metal 02" → 2).
  // Unnumbered categories sort first (0), preserving authored order otherwise.
  function categoryOrdinal(category) {
    var match = String(category.title || category.name || category.uuid || '').match(/(\d+)\s*$/);
    return match ? parseInt(match[1], 10) : 0;
  }

  // Ordered metal sources for the metal slots: [primary, secondary, …].
  //   primary   — a standalone "…Metal…" option value (combined-listing
  //               "Metal Type"), else the first metal named in the product title
  //   secondary — the compound option part whose sub-name contains "metal";
  //               when the product has no such option, the remaining metals
  //               named in the title, in title order (two-tone listings)
  // A real variant option beats the title for the secondary slots: it is an
  // explicit shopper choice, where the title is only the product's own naming.
  // Split convention matches the compound picker: NAME on ":", VALUE on " / ".
  function collectMetalValues(variant, categories) {
    var out = [];
    var titleMetals = [];

    // Primary metal: prefer a STANDALONE variant option whose name contains
    // "metal" (e.g. a combined-listing "Metal Type", where the metal is a real
    // option value, not in the product title). Fall back to the product title
    // (standalone per-metal products, whose title carries the metal).
    var primary = '';
    for (var p = 0; p < optionNames.length; p++) {
      var pname = optionNames[p];
      if (pname && pname.indexOf(':') === -1 && /metal/i.test(pname)) {
        var pval = variantOptionValue(variant, p + 1);
        if (pval) {
          primary = pval;
          break;
        }
      }
    }
    if (!primary) {
      titleMetals = resolveProductMetals(categories);
      primary = titleMetals[0] || '';
    }
    if (primary) out.push(primary);

    var fromOptions = [];
    for (var i = 0; i < optionNames.length; i++) {
      var name = optionNames[i];
      var value = variantOptionValue(variant, i + 1);
      if (!name || !value || name.indexOf(':') === -1) continue;
      var subNames = name.split(':');
      var subValues = value.split(' / ');
      for (var k = 0; k < subNames.length; k++) {
        if (/metal/i.test(subNames[k]) && subValues[k]) {
          fromOptions.push(subValues[k].trim());
        }
      }
    }
    return out.concat(fromOptions.length ? fromOptions : titleMetals.slice(1));
  }

  // Deliberate compatibility fallbacks, not dead code: authored iJewel scenes
  // differ by export version — the configurator plugin is registered under
  // different names across runtime builds, and categories arrive either
  // array- or object-keyed.
  function getVariationPlugin() {
    var viewer = window.iJewelActiveViewer;
    if (!viewer) return null;
    return (
      viewer.getPluginByType('VariationConfiguratorPlugin') ||
      viewer.getPluginByType('MaterialConfiguratorPlugin')
    );
  }

  // Flatten the plugin's variation categories whether `variations` is an array
  // or an object keyed by type (materials / objects / …).
  function getCategories(plugin) {
    if (!plugin || !plugin.variations) return [];
    if (Array.isArray(plugin.variations)) return plugin.variations;
    return Object.keys(plugin.variations).reduce(function (acc, key) {
      var arr = plugin.variations[key];
      return Array.isArray(arr) ? acc.concat(arr) : acc;
    }, []);
  }

  function categoryItems(category) {
    return category.materials || category.options || category.objects || [];
  }

  function findOption(category, value) {
    var target = normalize(value);
    return categoryItems(category).find(function (o) {
      return (
        normalize(o.name) === target ||
        (o.userData && normalize(o.userData.label) === target)
      );
    });
  }

  // Set `value` in every given category that has a matching option (a gem value
  // hits "Gem 01"; shape/size values match nothing and no-op). Metal categories
  // are handled separately by ordinal, so they're not passed here.
  async function applyValueInCategories(plugin, categories, value) {
    for (var i = 0; i < categories.length; i++) {
      var category = categories[i];
      var option = findOption(category, value);
      if (option) await applyOption(plugin, category, option);
    }
  }

  async function applyOption(plugin, category, option) {
    var viewer = plugin.viewer || window.iJewelActiveViewer;
    var optionId = option.uuid || option.name;
    try {
      await runWithInstantTransitions(viewer, async function () {
        await plugin.applyVariation(category, optionId, 'materials', true);
      });
    } catch (error) {
      console.error('iJewel 3D: failed to apply variation', error);
    }
  }

  // Combined-listing metal: metal is the product, not a variant axis. Find every
  // metal-category option whose authored name appears in the product title and
  // return them IN TITLE ORDER, so a two-tone title ("… - 9K Yellow Gold & 9K
  // White Gold") yields [primary, secondary]. Matches against the GLB's own
  // option names — no metal list is hardcoded here.
  //
  // Longest match wins on overlap, so "14K Rose Gold" beats a bare "Rose Gold"
  // at the same spot; a shorter name that merely NESTS inside an accepted one is
  // dropped even when it sits elsewhere in the string, otherwise "Rose Gold
  // Pendant - 14K Rose Gold" would report two different metals.
  function resolveProductMetals(categories) {
    var title = normalize(productTitle);
    if (!title) return [];

    var names = [];
    categories.forEach(function (category) {
      var label = category.title || category.name || category.uuid || '';
      if (!/metal/i.test(label)) return;
      categoryItems(category).forEach(function (o) {
        if (o.name && names.indexOf(o.name) === -1) names.push(o.name);
      });
    });

    var hits = [];
    names.forEach(function (name) {
      var n = normalize(name);
      if (!n) return;
      var from = 0;
      var at = title.indexOf(n, from);
      while (at !== -1) {
        hits.push({ name: name, norm: n, at: at, len: n.length });
        from = at + 1;
        at = title.indexOf(n, from);
      }
    });

    hits.sort(function (a, b) {
      return b.len - a.len || a.at - b.at;
    });

    var taken = [];
    hits.forEach(function (hit) {
      var clash = taken.some(function (t) {
        var overlaps = hit.at < t.at + t.len && t.at < hit.at + hit.len;
        var nested = t.norm.indexOf(hit.norm) !== -1;
        return overlaps || nested;
      });
      if (!clash) taken.push(hit);
    });

    return taken
      .sort(function (a, b) {
        return a.at - b.at;
      })
      .map(function (hit) {
        return hit.name;
      });
  }
})();
